import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { FOURKITES_CUSTOMER_APP } from '../data/mutations'

/**
 * Saves Turvo app's username, database and password.
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const FourKitesCustomerModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props

  const [formError, setFormError] = useState('')

  const { showToast } = Toast()

  const [enableFourKitesCustomerApp, { loading: enableAppLoading }] = useMutation<{
    enableFourKitesCustomerAppCredentials: { apiKey: string; appType: string; customerID?: string }
  }>(FOURKITES_CUSTOMER_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `FourKitesCustomer app is enabled successfully!`
      })
      setFormError('')
      refetchApps()
      closeModal()
    },
    onError: (err) => {
      if (err.message === 'Incorrect login credentials') {
        setFormError('Please enter correct API Key.')
        showToast({
          status: 'error',
          title: `Please enter correct API Key`
        })
      } else if (err.message === 'API key is required') {
        setFormError(`API key is required`)
        showToast({
          status: 'error',
          title: `API key is required`
        })
      }
    }
  })

  type FieldValues = {
    appApiKey?: string
    appCustomerID?: string
  }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    const { appApiKey, appCustomerID } = event
    enableFourKitesCustomerApp({
      variables: {
        apiKey: appApiKey,
        appType: appType,
        customerID: appCustomerID
      }
    })
  }

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/four-kites-customer/"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable ${appType} for Chaine`}
        subtitle={`Enabling this integration requires an API Key and optionally a Customer ID. Enter your API Key and optionally your Customer ID.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={enableAppLoading}
        footer={formError ? footer : <></>}
        modalItems={[
          {
            labelText: 'FourKitesCustomer Customer ID',
            name: 'appCustomerID',

            placeHolder: 'Enter the FourKitesCustomer Customer ID',
            type: 'input'
          },
          {
            labelText: 'FourKitesCustomer API Key',
            name: 'appApiKey',

            placeHolder: 'Enter the FourKitesCustomer API Key',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

FourKitesCustomerModal.displayName = 'FourKitesCustomerModal'

export { FourKitesCustomerModal }
